import { SiteType } from '../graphql/codegen';

import { Environments } from '.';

const CONTAINER_ID_GSTAR = 'GTM-PPGBGR8';
const CONTAINER_ID_OUTLET = 'GTM-WZK9JMN';
const CONTAINER_ID_AAF = 'GTM-P7LKQM5X';

type GtmConfig = { containerId: string; authId?: string; prevId?: string };

const gtmStagingConfigGstar = {
  containerId: CONTAINER_ID_GSTAR,
  authId: 'bh-lXppDHi_K7lhhZaRzLw',
  prevId: 'env-5',
};

const gtmAcceptanceConfigGstar = {
  containerId: CONTAINER_ID_GSTAR,
  authId: 'M5qKMvr22Rnw_e7oKkn7og',
  prevId: 'env-7',
};

const gtmStagingConfigOutlet = {
  containerId: CONTAINER_ID_OUTLET,
  authId: 'QuB_hYykmiOkOepdOVypaQ',
  prevId: 'env-3',
};

const gtmAcceptanceConfigOutlet = {
  containerId: CONTAINER_ID_OUTLET,
  authId: 'AxJFh6pscledTT6vsT-9JA',
  prevId: 'env-5',
};

const gtmStagingConfigAaf = {
  containerId: CONTAINER_ID_AAF,
  authId: 'M-4xf7TOaURoNyikmD2qgg',
  prevId: 'env-3',
};

const gtmAcceptanceConfigAaf = {
  containerId: CONTAINER_ID_AAF,
  authId: 'mvWD7u9gk4iZswQZ784RvA',
  prevId: 'env-4',
};

const gtmProductionConfigGstar = { containerId: CONTAINER_ID_GSTAR };
const gtmProductionConfigOutlet = { containerId: CONTAINER_ID_OUTLET };
const gtmProductionConfigAaf = { containerId: CONTAINER_ID_AAF };

const createGtmConfig = (
  stagingConfig: GtmConfig,
  acceptanceConfig: GtmConfig,
  productionConfig: GtmConfig
): Record<Environments, GtmConfig> => ({
  [Environments.DEV]: stagingConfig,
  [Environments.TST]: stagingConfig,
  [Environments.ACC]: acceptanceConfig,
  [Environments.PRD]: productionConfig,
});

export const gtmIds: Record<SiteType, ReturnType<typeof createGtmConfig>> = {
  [SiteType.Aaf]: createGtmConfig(
    gtmStagingConfigAaf,
    gtmAcceptanceConfigAaf,
    gtmProductionConfigAaf
  ),
  [SiteType.Gstar]: createGtmConfig(
    gtmStagingConfigGstar,
    gtmAcceptanceConfigGstar,
    gtmProductionConfigGstar
  ),
  [SiteType.Employeeshop]: createGtmConfig(
    gtmStagingConfigGstar,
    gtmAcceptanceConfigGstar,
    gtmProductionConfigGstar
  ),
  [SiteType.Outlet]: createGtmConfig(
    gtmStagingConfigOutlet,
    gtmAcceptanceConfigOutlet,
    gtmProductionConfigOutlet
  ),
  [SiteType.Wahts]: createGtmConfig(
    gtmStagingConfigGstar,
    gtmAcceptanceConfigGstar,
    gtmProductionConfigGstar
  ),
};

export const getGtmConfig = (siteType: SiteType, environment: Environments): GtmConfig =>
  gtmIds[siteType][environment];

export const GOOGLE_TAG_MANAGER_HOST = 'www.googletagmanager.com';

/**
 * First-party measurement path for the Google Tag Gateway (DAN-1487).
 * Akamai serves this path on the site origin and proxies it to gtm-<containerId>.fps.goog,
 * so gtm.js and all GA4 / Google Ads hits stay on our own domain.
 * @see https://developers.google.com/tag-platform/tag-manager/gateway/setup-guide?setup=manual
 */
export const GOOGLE_TAG_GATEWAY_PATH = '/clt/';

/**
 * The gateway is enabled by the (legacy-named) OCC toggle `enableCLTAnalytics`.
 * Only PRD sits behind Akamai, so on any other environment the `/clt/` path does not exist
 * and we always fall back to www.googletagmanager.com regardless of the toggle.
 */
export const isTagGatewayEnabled = (
  enableCLTAnalytics: boolean | null | undefined,
  environment: Environments
): boolean => !!enableCLTAnalytics && environment === Environments.PRD;

/** Non-production environments load a GTM preview environment via gtm_auth/gtm_preview. */
export const getGtmQueryParams = (
  environment: Environments,
  { authId, prevId }: GtmConfig
): string =>
  environment === Environments.PRD
    ? ''
    : `&gtm_auth=${authId}&gtm_preview=${prevId}&gtm_cookies_win=x`;

/**
 * Base URL of the gtm.js loader; the GTM snippet appends `?id=<containerId>` itself.
 * With the tag gateway enabled the container is served relative to the current origin.
 */
export const getGtmScriptBaseUrl = (useTagGateway: boolean): string =>
  useTagGateway ? GOOGLE_TAG_GATEWAY_PATH : `https://${GOOGLE_TAG_MANAGER_HOST}/gtm.js`;

/**
 * The Google Tag Gateway does not support the <noscript> snippet,
 * so the iframe always loads from Google directly regardless of the gateway toggle.
 */
export const getGtmNoScriptSrc = (containerId: string, gtmParams: string): string =>
  `https://${GOOGLE_TAG_MANAGER_HOST}/ns.html?id=${containerId}${gtmParams}`;
